import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  FormGroupDirective,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { CompanyAddressFormModel } from '@registration/interfaces';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, of, switchMap } from 'rxjs';
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { filter, tap } from 'rxjs/operators';
import { CountryEnum } from '../../../../shared/enums/country-enum';

// hidden address parts, filled only by selecting a suggestion
const REQUIRED_ADDRESS_PARTS = ['street', 'streetNumber', 'zipCode', 'city'];
// parts that change when an address is selected or set by the parent (not by typing)
const SELECTED_ADDRESS_PARTS = [...REQUIRED_ADDRESS_PARTS, 'lat', 'lng'];
const MIN_SEARCH_LENGTH = 6;

interface AddressSelectionState {
  // text of the last selected (or programmatically set) address
  confirmedAddress: unknown;
  // hidden parts belonging to confirmedAddress
  selectedParts: string;
}

// HOE-1034: kept per form control (not per component instance), so it survives
// re-creation of the component, e.g. "Zurück"/"Weiter" in the H2 registration
const selectionStates = new WeakMap<AbstractControl, AddressSelectionState>();

@UntilDestroy()
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-address-search-autocomplete',
  templateUrl: './address-search-autocomplete.component.html',
  styleUrls: ['./address-search-autocomplete.component.scss'],
})
export class AddressSearchAutocompleteComponent
  implements OnInit, AfterViewInit, DoCheck, OnDestroy
{
  @Input({ required: true })
  public title: string;
  @Input({ required: false })
  public withClearIcon?: boolean = false;
  @Input({ required: false })
  public tabOrder?: number;
  @Input({ required: false })
  public isCo2: boolean = false;

  @ViewChild(MatAutocompleteTrigger)
  public autocompleteTrigger: MatAutocompleteTrigger;

  public foundedPlaces$ = new BehaviorSubject(null);

  public addressForm: FormGroup<CompanyAddressFormModel>;
  public countryEnum = CountryEnum;

  private Place = null;
  private lastAddressState: string = null;

  public constructor(
    private readonly parentForm: FormGroupDirective,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {}

  public get addressControl(): AbstractControl {
    return this.addressForm?.get('address');
  }

  public async ngOnInit(): Promise<void> {
    if (this.parentForm?.control) {
      this.addressForm = this.parentForm.control?.get(
        'companyAddress'
      ) as FormGroup;
      if (
        this.addressControl &&
        (!selectionStates.has(this.addressControl) ||
          this.getSelectedParts() !== this.selectionState.selectedParts)
      ) {
        // current value was set by the parent (prefilled, empty, or changed while
        // this component did not exist), so it counts as confirmed
        this.confirmCurrentAddress();
      }
      this.addressControl?.addValidators(this.addressSelectedValidator);
      this.addressControl?.updateValueAndValidity({ emitEvent: false });
      // address is validated before its siblings on setValue/patchValue of the group,
      // so validate it again once the whole group has changed
      this.addressForm?.valueChanges
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          if (this.getSelectedParts() !== this.selectionState?.selectedParts) {
            // hidden parts changed: address was set by the parent, not typed
            this.confirmCurrentAddress();
          }
          this.addressControl.updateValueAndValidity({ emitEvent: false });
        });
      if (this.addressControl?.value) {
        // prefilled address (edit mode): show errors of incomplete data directly
        this.addressControl.markAsTouched();
      }
    }
    this.changeDetectorRef.detectChanges();

    const { Place } = (await google?.maps?.importLibrary(
      'places'
    )) as google.maps.PlacesLibrary;
    this.Place = Place;
  }

  public ngAfterViewInit() {
    this.addressForm
      .get('address')
      .valueChanges.pipe(
        untilDestroyed(this),
        tap(value => {
          if (!(value?.length >= MIN_SEARCH_LENGTH)) {
            // suggestions of an older (longer) search text must not be used any more
            this.foundedPlaces$.next([]);
          }
        }),
        filter(value => value?.length >= MIN_SEARCH_LENGTH),
        // no (paid) Places request for an address that was just selected or set by the parent
        filter(value => value !== this.selectionState?.confirmedAddress),
        switchMap(value => {
          if (value) {
            if (!this.Place) return of([]);
            return this.Place?.searchByText({
              fields: ['formattedAddress', 'addressComponents', 'location'],
              textQuery: value,
            });
          }
          return of([]);
        })
      )
      .subscribe(({ places }) => {
        this.foundedPlaces$.next(places);
      });
  }

  public ngDoCheck(): void {
    // OnPush: the parent may mark the control as touched (e.g. markAllAsTouched on "Weiter")
    const state = `${this.addressControl?.touched}|${this.addressControl?.status}`;
    if (state !== this.lastAddressState) {
      this.lastAddressState = state;
      this.changeDetectorRef.markForCheck();
    }
  }

  public ngOnDestroy(): void {
    this.addressControl?.removeValidators(this.addressSelectedValidator);
    this.addressControl?.updateValueAndValidity({ emitEvent: false });
  }

  public async onKeydownEnter() {
    const isHighlightedOptionSelectedByAutocomplete =
      this.autocompleteTrigger?.panelOpen && !!this.autocompleteTrigger.activeOption;
    const isAlreadySelected =
      !!this.addressControl?.value &&
      this.addressControl.value === this.selectionState?.confirmedAddress;
    if (isHighlightedOptionSelectedByAutocomplete || isAlreadySelected) {
      // e.g. arrow keys + enter: the autocomplete selects the highlighted suggestion itself
      // (depending on the listener order before or after this handler) - don't overwrite it
      return;
    }
    const places = this.foundedPlaces$.getValue();
    if (places?.length > 0) {
      this.setAddressForm(places[0]);
    } else {
      this.addressControl?.markAsTouched();
    }
  }

  public onOptionSelected(event: MatAutocompleteSelectedEvent) {
    const place = event.option.value as google.maps.places.Place;
    this.setAddressForm(place);
  }

  private get selectionState(): AddressSelectionState {
    return selectionStates.get(this.addressControl);
  }

  private confirmCurrentAddress(): void {
    selectionStates.set(this.addressControl, {
      confirmedAddress: this.addressControl.value,
      selectedParts: this.getSelectedParts(),
    });
  }

  private getSelectedParts(): string {
    return JSON.stringify(
      SELECTED_ADDRESS_PARTS.map(part => this.addressForm.get(part)?.value)
    );
  }

  private setAddressForm(place: google.maps.places.Place) {
    const addressComponent = place.addressComponents;
    // confirm the text before setting it, so that setting it starts no new search
    selectionStates.set(this.addressControl, {
      confirmedAddress: place.formattedAddress,
      selectedParts: this.selectionState?.selectedParts,
    });
    this.addressForm.setValue(
      {
        address: place.formattedAddress,
        street: this.getAddressComponentByType(addressComponent, 'route'),
        streetNumber: this.getAddressComponentByType(
          addressComponent,
          'street_number'
        ),
        city: this.getAddressComponentByType(addressComponent, 'locality') !== ''
              ? this.getAddressComponentByType(addressComponent, 'locality')
              : this.getAddressComponentByType(addressComponent, 'political'),
        zipCode: this.getAddressComponentByType(
          addressComponent,
          'postal_code'
        ),
        lat: place.location.lat(),
        lng: place.location.lng(),
        countryId: Number(this.countryEnum[this.getAddressComponentByType(
          addressComponent,
          'country',
          'shortText'
        )]),
      },
      { onlySelf: true }
    );
    // also when the same address is selected again (hidden parts unchanged)
    this.confirmCurrentAddress();
    // group was set with onlySelf, so also update the parents
    this.addressControl.updateValueAndValidity();
    this.addressControl.markAsTouched();
  }

  // Validator of the visible search field: empty is handled by "required" in the template
  private readonly addressSelectedValidator: ValidatorFn = (
    control: AbstractControl
  ): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }
    if (control.value !== selectionStates.get(control)?.confirmedAddress) {
      return { addressNotSelected: true };
    }
    const group = control.parent;
    const invalidPart = REQUIRED_ADDRESS_PARTS.find(
      part => group?.get(part)?.invalid
    );
    return invalidPart ? { addressIncomplete: invalidPart } : null;
  };

  private getAddressComponentByType(
    addressComponent: google.maps.places.AddressComponent[],
    type: string,
    textForm: string = "longText"
  ) {
    const types = addressComponent.filter(x => x?.types.includes(type));
    if (types.length > 0) {
      return types[0]?.[textForm];
    } else {
      return '';
    }
  }
}
